import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Menu, Flame, User, MessagesSquare, LogOut, Wallet, Bell } from "lucide-react";

import { Button } from "@/components/ui/button";
import { UnreadBadge } from "@/components/unread-badge";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationBell } from "@/components/notification-bell";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { getWalletOverview } from "@/lib/wallets.functions";
import { formatHeat } from "@/lib/behot";
import { LanguageSwitcher } from "@/components/language-switcher";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/i18n";
import type { TranslationKey } from "@/i18n/dictionaries/en";



const NAV = [
  { to: "/hot", key: "nav.hot" },
  { to: "/categories", key: "nav.categories" },
  { to: "/marketplace", key: "nav.marketplace" },
  { to: "/fund", key: "nav.fund", soon: true },
  { to: "/promote", key: "nav.promote" },
  { to: "/earn", key: "nav.earn" },
  { to: "/referrals", key: "nav.referrals" },
] as const;

const MEMBER_NAV = [] as const;


export function SiteHeader() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const [mobileOpen, setMobileOpen] = useState(false);
  const unreadMessages = useUnreadMessages();
  const { unread: unreadNotifications } = useNotifications(30);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const overviewFn = useServerFn(getWalletOverview);

  const { data: wallet } = useQuery({
    queryKey: ["header-wallet", user?.id],
    queryFn: () => overviewFn(),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
  const { data: headerProfile } = useQuery({
    queryKey: ["header-profile", user?.id],
    enabled: !!user,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("avatar_url,display_name")
        .eq("id", user!.id)
        .maybeSingle();
      return data as { avatar_url: string | null; display_name: string | null } | null;
    },
  });

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/login", replace: true });
  }

  const totalHeatCents =
    (wallet?.balances.topupCents ?? 0) + (wallet?.balances.referralCents ?? 0);


  return (

    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
      <div className="mx-auto flex h-20 max-w-6xl items-center gap-3 px-4">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label={t("nav.home")}>
          <picture className="block shrink-0">
            <source srcSet="/brand/icon-96.webp 96w, /brand/icon-192.webp 192w" sizes="48px" type="image/webp" />
            <img
              src="/brand/icon-96.png"
              srcSet="/brand/icon-96.png 96w, /brand/icon-192.png 192w"
              sizes="48px"
              width={48}
              height={48}
              alt=""
              decoding="async"
              fetchPriority="high"
              className="h-11 w-11 object-contain sm:h-12 sm:w-12"
            />
          </picture>
          <span className="font-display text-2xl font-extrabold tracking-tight sm:text-[1.75rem]">
            <span className="text-gradient-hot">BeHot</span>
            <span className="text-foreground">.Now</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {[...NAV, ...(user ? MEMBER_NAV : [])].map(
            (item: { to: string; key: string; soon?: boolean }) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                activeProps={{ className: "text-foreground bg-muted" }}
              >
                {t(item.key as TranslationKey)}
                {item.soon && (
                  <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-foreground">
                    {t("nav.soon")}
                  </span>
                )}
              </Link>
            ),
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <LanguageSwitcher className="hidden shrink-0 sm:inline-flex" />

          {!loading && !user && (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/login">{t("nav.login")}</Link>
            </Button>
          )}

          <Button asChild variant="hot" size="sm">
            <Link to="/get-hot">
              <Flame className="size-4" /> {t("nav.getHot")}
            </Link>
          </Button>

          {!loading && user && <NotificationBell className="hidden sm:inline-flex" />}
          {!loading && user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer rounded-full ring-offset-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 inline-flex"
                  aria-label={t("nav.dashboard")}
                >
                  <Avatar className="size-9 border border-border">
                    {headerProfile?.avatar_url ? (
                      <AvatarImage src={headerProfile.avatar_url} alt="" />
                    ) : null}
                    <AvatarFallback className="bg-muted text-xs font-semibold">
                      {(headerProfile?.display_name || user.email || "?").trim().charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/app/wallets" className="flex items-center gap-2 cursor-pointer">
                    <Wallet className="size-4 text-primary" />
                    <span className="font-display font-bold">{formatHeat(totalHeatCents)}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{t("nav.walletBalance")}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/messages" className="flex items-center gap-2 cursor-pointer">
                    <MessagesSquare className="size-4" />
                    {t("nav.messages")}
                    <UnreadBadge count={unreadMessages} className="ml-auto" />
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/notifications" className="flex items-center gap-2 cursor-pointer">
                    <Bell className="size-4" />
                    {t("messaging.notifications")}
                    <UnreadBadge count={unreadNotifications} className="ml-auto" />
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app" className="flex items-center gap-2 cursor-pointer">
                    <User className="size-4" />
                    {t("nav.profile")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="size-4" />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={t("nav.menu")}>
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="mt-10 flex flex-col gap-1">
                <div className="flex items-center gap-2 px-3 pb-2 sm:hidden">
                  <LanguageSwitcher />
                  <span className="text-sm text-muted-foreground">{t("lang.select")}</span>
                </div>
                {[...NAV, ...(user ? MEMBER_NAV : [])].map(
                  (item: { to: string; key: string; soon?: boolean }) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 rounded-lg px-3 py-3 text-base font-medium text-foreground hover:bg-muted"
                    >
                      {t(item.key as TranslationKey)}
                      {item.soon && (
                        <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-foreground">
                          {t("nav.soon")}
                        </span>
                      )}
                    </Link>
                  ),
                )}
                <Link
                  to={user ? "/app" : "/login"}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-medium hover:bg-muted"
                >
                  {user ? t("nav.dashboard") : t("nav.login")}
                </Link>
              </nav>
            </SheetContent>
          </Sheet>

        </div>
      </div>
    </header>
  );
}
